import {
	Configuration,
	RedirectRequest,
	SilentRequest,
	PublicClientApplication,
} from "@azure/msal-browser";
import { Buffer } from "buffer";
import isEmail from "validator/lib/isEmail";
import isNumeric from "validator/lib/isNumeric";
import { staffProfile } from "../auth/authConfig.d";

export const msalConfig: Configuration = {
	auth: {
		clientId: process.env.REACT_APP_AUTH_CLIENT_ID ?? "",
		redirectUri: window.location.origin,
		authority: process.env.REACT_APP_AUTH_AUTHORITY,
	},
};
export const msalInstance = new PublicClientApplication(msalConfig);

export const loginRequest: RedirectRequest = {
	scopes: ["User.Read"],
};

export const acquireAccessToken = async (): Promise<string> => {
	const accounts = msalInstance.getAllAccounts();
	const request: SilentRequest = {
		scopes: [process.env.REACT_APP_API_SCOPE ?? ""],
		redirectUri: window.location.origin,
		account: accounts[0],
	};
	const requestToken = await msalInstance.acquireTokenSilent(request);

	return Promise.resolve(requestToken.accessToken);
};

export const getUserProfileAsync = async (accessToken: string): Promise<staffProfile> => {
	const graphProfileEndpoint = "https://graph.microsoft.com/v1.0/me";

	const profileFields = [
		"displayName",
		"givenName",
		"surname",
		"jobTitle",
		"officeLocation",
		"employeeId",
		"onPremisesExtensionAttributes",
		"usageLocation",
		"country",
		"mail",
	];

	const jbProfileRequestEndpoint = graphProfileEndpoint + "?$select=" + profileFields.join(",");
	const graphProfile = await fetchAuthenticatedUrl(jbProfileRequestEndpoint, accessToken);
	const staffProfile: staffProfile = {
		staffId: getStaffId(graphProfile),
		storeLocation: "",
		storeNumber: 0,
		officeLocation: graphProfile.officeLocation,
		displayName: graphProfile.displayName,
		givenName: graphProfile.givenName,
		surname: graphProfile.surname,
		roles: [],
	};

	if (graphProfile.onPremisesExtensionAttributes && graphProfile.onPremisesExtensionAttributes.extensionAttribute9) {
		const countryAndStoreNumber = graphProfile.onPremisesExtensionAttributes.extensionAttribute9; // in the format "AU10" for JB; "10" for TGG

		return {
			...staffProfile,
			storeLocation: countryAndStoreNumber.match(/^([A-Za-z]+)/) ? countryAndStoreNumber.match(/^([A-Za-z]+)/)[0] : "",
			storeNumber: parseInt(countryAndStoreNumber.match(/([0-9]+)$/)[0]),
		};
	}
	return staffProfile;
};

const fetchAuthenticatedUrl = async (url: string, accessToken: string): Promise<any> => {
	const response = await fetch(url, {
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	});
	return response.json();
};

const getStaffId = (graphProfile: any): any => {
	const extensionAttribute15 = graphProfile.onPremisesExtensionAttributes?.extensionAttribute15;
	const { employeeId } = graphProfile;

	const staffIdFromEmployeeId = extensionAttribute15
		? extensionAttribute15
		: employeeId
		? parseInt(employeeId, 10).toString()
		: null;

	const staffIdFromEmail = getStaffIdFromEmail(graphProfile.mail);

	if (!(staffIdFromEmail || staffIdFromEmployeeId)) {
		throw new Error("User does not have email and employeeId");
	} else {
		return staffIdFromEmployeeId ?? staffIdFromEmail;
	}
};

const getStaffIdFromEmail = (email: string): string | null => {
	if (!(email && isEmail(email))) {
		return null;
	}
	const emailPrefix = email.substring(0, email.indexOf("@"));
	if (!isNumeric(emailPrefix)) {
		return null;
	}
	return emailPrefix;
};

export const getUserPhotoAsync = async (accessToken: string): Promise<string | null> => {
	const graphProfileEndpoint = "https://graph.microsoft.com/v1.0/me/photo/$value";
	const photoBinary = await fetchPhoto(graphProfileEndpoint, accessToken);

	const photo = photoBinary ? Buffer.from(photoBinary).toString("base64") : null;

	return photo;
};

const fetchPhoto = async (url: string, accessToken: string) => {
	const response = await fetch(url, {
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	});

	if (response.status === 200) {
		return response.arrayBuffer();
	} else {
		return null;
	}
};
